<template>
    <!-- Show Welcome component for unauthenticated users -->
    <Welcome v-if="!isAuthenticated"/>

    <!-- Dashboard for authenticated users -->
    <div v-else class="home-page">
        <div class="page-container">
            <!-- STATE A: Company Selection Screen -->
            <div v-if="!selectedGlobalCompany" class="company-selection-screen">
                <div class="selection-card">
                    <div class="selection-icon">
                        <img src="/img/logo_quare.svg" alt="Invoice Scan" class="selection-logo" />
                    </div>
                    <h1 class="selection-title">
                        <!-- The product's name is not translated, so it is written
                             here rather than carried through five locales. -->
                        <span class="selection-brand">Invoice Scan</span>
                        <span class="selection-greeting">{{ $t(authStore.isFirstLogin ? 'common.greetingFirst' : 'common.greetingBack') }}</span>
                    </h1>

                    <div class="selection-selector">
                        <CompanySelector
                            v-model="tempCompanyId"
                            :placeholder="$t('companies.selectCompany')"
                            :show-label="false"
                            @change="handleCompanySelection"
                        />
                    </div>

                    <p class="selection-hint">
                        {{ $t('companies.noCompanyYet') }}
                        <button class="link-button" @click="() => router.push('/companies')">
                            {{ $t('companies.createHere') }}
                        </button>
                    </p>
                </div>
            </div>

            <!-- STATE B: Dashboard (Company Selected) -->
            <section v-else class="scans-section">
                <!-- One row across the top: who is being worked in, how a
                     document gets in, and the code for doing it from a phone.
                     Three cards of the same make, the form in the middle
                     taking whatever the two columns leave. -->
                <div class="upload-section">
                    <div
                        class="upload-row"
                        :class="{
                            'upload-row--collapsed': !uploadFormVisible,
                            'upload-row--queued': hasQueuedUploads,
                        }"
                    >
                        <!-- Company -->
                        <div class="company-header-bar">
                            <div class="company-info">
                                <div class="company-name-badge">
                                    <span class="company-name">{{ selectedCompanyName }}</span>

                                    <!-- What it is registered as: label and value. -->
                                    <div v-if="companyRegistry.length" class="company-details">
                                        <span v-for="detail in companyRegistry" :key="detail.label" class="company-detail">
                                            <span class="company-detail-label">{{ detail.label }}</span>
                                            <span class="company-detail-value">{{ detail.value }}</span>
                                        </span>
                                    </div>

                                    <!-- How to reach it: the lines speak for themselves. -->
                                    <div v-if="companyContact.length" class="company-contact">
                                        <span v-for="line in companyContact" :key="line" class="company-contact-line">{{ line }}</span>
                                    </div>
                                </div>
                                <!-- No hover label on this one: it says what it does. -->
                                <button class="btn-switch-company" :aria-label="$t('companies.switchCompany')" @click="switchCompany">
                                    <LineIcon name="arrow-left-right" :size="14" :stroke-width="1.8" />
                                    <span class="btn-switch-label">{{ $t('companies.switchCompany') }}</span>
                                </button>
                            </div>
                        </div>

                        <div class="upload-row__form">
                            <UploadQueue
                                ref="uploadQueueRef"
                                :company-name="selectedCompanyName"
                                :selected-company-id="selectedGlobalCompany"
                                @scan-completed="refreshScans"
                                @register-completed="handleRegisterCompleted"
                                @company-change="handleUploadQueueCompanyChange"
                                @form-visibility="uploadFormVisible = $event"
                            />
                        </div>

                        <!-- QR Code. Not on a phone: the code is there to be
                             scanned by one, so on one it is a picture of a
                             door you are already standing in. Not on a narrow
                             window either, where the cards stack and it would
                             cost a screenful.
                             Gone while the form is hidden too: it is a way of
                             uploading, and nothing is being uploaded. -->
                        <section v-if="showQrCode" class="qr-code-section">
                            <div class="qr-code-card">
                                <img v-if="qrCodeDataUrl" :src="qrCodeDataUrl" alt="QR Code" class="qr-code-image"/>
                                <div v-else class="qr-code-placeholder">
                                    <div class="spinner"></div>
                                    <p>Loading QR Code...</p>
                                </div>
                            </div>
                            <div class="qr-info">
                                <h3 class="qr-title">{{ $t('common.scanToAccessMobile') }}</h3>
                                <p class="qr-description">{{ $t('common.scanQRDescription') }}</p>
                            </div>
                        </section>
                    </div>
                </div>

                <!-- Persistent register notice: which invoices are missing + a
                     button to re-scan ONLY those pages (not the whole register). -->
                <div v-if="registerNotice" class="register-notice">
                    <div class="register-notice__icon">⚠️</div>
                    <div class="register-notice__body">
                        <template v-if="registerNotice.missing_count > 0">
                            <div class="register-notice__title">
                                Липсват {{ registerNotice.missing_count }} фактури от последния регистър
                            </div>
                            <div class="register-notice__nums">{{ (registerNotice.missing_numbers || []).join(', ') }}</div>
                            <div class="register-notice__hint">
                                Натиснете „Досканирай липсващите“ — ще се сканират само страниците с тези номера, не целият регистър.
                            </div>
                        </template>
                        <template v-else>
                            <div class="register-notice__title">Сумата не съвпада с „Сумарно“</div>
                            <div class="register-notice__hint">
                                Получена {{ registerNotice.extracted_with_vat }}, очаквана {{ registerNotice.expected_with_vat }} —
                                възможен дубликат или грешно разчетена сума.
                            </div>
                        </template>
                    </div>
                    <div class="register-notice__actions">
                        <button
                            v-if="registerNotice.missing_count > 0"
                            class="register-notice__btn"
                            :disabled="rescanningMissing"
                            @click="rescanMissingInvoices"
                        >
                            <font-awesome-icon v-if="rescanningMissing" icon="fa-solid fa-spinner" spin />
                            {{ rescanningMissing ? 'Досканиране…' : 'Досканирай липсващите' }}
                        </button>
                        <button class="register-notice__dismiss" @click="dismissRegisterNotice" aria-label="Затвори">×</button>
                    </div>
                </div>

                <!-- Table Container -->
                <div class="table-container">
                    <!-- Three cards, not one: what is being looked at, what
                         is wrong with it, and the documents themselves. They
                         were one box with rules drawn across it, which made
                         the warnings read as a row of the table. -->
                    <section class="panel panel--controls">
                    <!-- Sort & Export Actions Row - Always Visible.
                         The company filter sits here beside the document type
                         rather than in a band of its own above: the two are
                         picked together and were a row apart for no reason. -->
                    <div class="actions-row">
                        <div class="company-selector-group">
                            <label class="sort-label">{{ $t('invoices.company').toUpperCase() }}</label>
                            <CompanySelector
                                v-model="topLevelCompanyId"
                                :class="{ 'active-filter': topLevelCompanyId }"
                                :placeholder="$t('companies.allCompanies')"
                                :show-label="false"
                                @change="handleTopLevelCompanyChange"
                            />
                        </div>

                        <div class="type-selector-group">
                            <label class="sort-label">{{ $t('scanDetails.documentType', 'Document Type').toUpperCase() }}</label>
                            <TypeSelector
                                v-model="filterManager.filters.value.type"
                                :class="{ 'active-filter': filterManager.filters.value.type }"
                                :show-label="false"
                                @change="applyFilters"
                            />
                        </div>

                        <div class="filter-export-group">
                            <button :class="{ active: showFilters }" class="btn-filter"
                                    @click="showFilters = !showFilters"
                                    title="Toggle Filters">
                                <LineIcon name="filter" :size="16" :stroke-width="2" />
                                <span class="btn-filter-text">{{ $t('common.filter') }}</span>
                            </button>

                            <div class="export-actions">
                                <button class="btn-export-single" @click="openExportAllModal">
                                    <LineIcon name="download" :size="16" :stroke-width="2" />
                                    {{ $t('common.exportAll') }}
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Filters Row -->
                    <div v-show="showFilters" class="filters-row">
                        <div class="filter-item">
                            <label class="filter-label">{{ $t('common.status').toUpperCase() }}</label>
                            <CustomSelect 
                                v-model="filterManager.filters.value.status" 
                                :options="statusOptions"
                                :class="{ 'active-filter': filterManager.filters.value.status }"
                                @change="applyFilters"
                            />
                        </div>

                        <div class="filter-item">
                            <label class="filter-label">{{ $t('invoices.vendor').toUpperCase() }}</label>
                            <VendorSelector
                                v-model="filterManager.filters.value.vendorId"
                                :class="{ 'active-filter': filterManager.filters.value.vendorId }"
                                :company-id="topLevelCompanyId"
                                :placeholder="$t('vendors.allVendors')"
                                :show-label="false"
                                @change="applyFilters"
                            />
                        </div>

                        <div class="filter-item">
                            <label class="filter-label">{{ $t('invoices.type').toUpperCase() }}</label>
                            <TypeSelector
                                v-model="filterManager.filters.value.type"
                                :class="{ 'active-filter': filterManager.filters.value.type }"
                                :show-label="false"
                                @change="applyFilters"
                            />
                        </div>

                        <div class="filter-item">
                            <label class="filter-label">{{ $t('invoices.checked').toUpperCase() }}</label>
                            <CustomSelect 
                                v-model="filterManager.filters.value.isChecked" 
                                :options="checkedOptions"
                                :class="{ 'active-filter': filterManager.filters.value.isChecked !== null && filterManager.filters.value.isChecked !== '' }"
                                @change="applyFilters"
                            />
                        </div>

                        <div class="filter-item">
                            <label class="filter-label">{{ $t('invoices.dateFrom').toUpperCase() }}</label>
                            <DateField
                                v-model="filterManager.filters.value.dateFrom"
                                :class="{ 'active-filter': filterManager.filters.value.dateFrom }"
                                class="filter-date"
                                @change="applyFilters"
                            />
                        </div>

                        <div class="filter-item">
                            <label class="filter-label">{{ $t('invoices.dateTo').toUpperCase() }}</label>
                            <DateField
                                v-model="filterManager.filters.value.dateTo"
                                :class="{ 'active-filter': filterManager.filters.value.dateTo }"
                                class="filter-date"
                                @change="applyFilters"
                            />
                        </div>

                        <div class="filter-item">
                            <label class="filter-label">{{ $t('invoices.uploadDate').toUpperCase() }}</label>
                            <DateField
                                v-model="filterManager.filters.value.uploadDate"
                                :class="{ 'active-filter': filterManager.filters.value.uploadDate }"
                                class="filter-date"
                                @change="applyFilters"
                            />
                        </div>

                        <div class="filter-item">
                            <label class="filter-label">{{ $t('common.archived').toUpperCase() }}</label>
                            <CustomSelect 
                                v-model="filterManager.filters.value.showArchived" 
                                :options="archivedOptions"
                                :class="{ 'active-filter': filterManager.filters.value.showArchived }"
                                @change="applyFilters"
                            />
                        </div>

                        <div class="filter-item">
                            <label class="filter-label">{{ $t('invoices.paymentMethod').toUpperCase() }}</label>
                            <CustomSelect 
                                v-model="filterManager.filters.value.paymentMethod" 
                                :options="paymentMethodOptions"
                                :class="{ 'active-filter': filterManager.filters.value.paymentMethod }"
                                @change="applyFilters"
                            />
                        </div>

                        <div class="filter-item filter-clear-container">
                            <label class="filter-label">&nbsp;</label>
                            <button class="btn-clear-filters" @click="clearFilters">
                                <LineIcon name="x-circle" :size="15" :stroke-width="1.8" />
                                {{ $t('invoices.clearFilters') }}
                            </button>
                        </div>
                    </div>

                    </section>

                    <!-- Where the table's warning banners land. They are
                         rendered inside ScansTable, which knows which scans
                         are in trouble, but belong between the filters and
                         the documents rather than inside either. -->
                    <div id="dashboard-alerts" class="alerts-slot"></div>

                    <section class="panel">
                    <!-- Only when something is selected. The select-all box
                         moved into the table's own header row, where the
                         column it belongs to already is, and the totals that
                         used to sit here are the card below this one. -->
                    <div v-if="selection.selectedCount.value > 0" class="select-row">
                        <div class="bulk-actions">
                            <span class="selected-count">{{ selection.selectedCount.value }} {{ $t('common.selected') }}</span>
                            <button class="btn-bulk btn-bulk-export" @click="exportScans()">
                                <LineIcon name="download" :size="15" :stroke-width="1.8" />
                                {{ $t('common.export') }}
                            </button>
                            <button class="btn-bulk btn-bulk-archive" @click="archiveSelected">
                                <LineIcon name="archive" :size="15" :stroke-width="1.8" />
                                {{ $t('common.archive') }}
                            </button>
                            <button class="btn-bulk btn-bulk-delete" @click="deleteSelected">
                                <LineIcon name="trash" :size="15" :stroke-width="1.8" />
                                {{ $t('common.delete') }}
                            </button>
                        </div>
                    </div>

                    <!-- Scans Table -->
                    <ScansTable
                        :loading="loading"
                        :queue-items="queueItems"
                        :scans="scans"
                        :selected-scans="selection.selectedItems.value"
                        :validation-warnings="validationWarnings"
                        :company-name="selectedCompanyName"
                        :enforce-subtotal-validation="authStore.enforceSubtotalValidation"
                        :validate-vat-conflicts="authStore.validateVatConflicts"
                        :empty-title="$t('common.noScansFound')"
                        :empty-message="$t('common.uploadFirstInvoice')"
                        :empty-action-text="$t('common.startScanning')"
                        :sort-by="filterManager.filters.value.sort"
                        :sort-order="filterManager.filters.value.order"
                        @delete="handleDeleteScan"
                        @export="handleExportScan"
                        @toggle-selection="toggleSelect"
                        @toggle-select-all="toggleSelectAll"
                        @empty-action="() => uploadQueueRef?.revealUploadForm()"
                        @cancel-upload="cancelUpload"
                        @type-change="handleTypeChange"
                        @sort-change="handleSortChange"
                    />
                    </section>

                    <!-- The totals, in a card of their own. They are worked
                         out in ScansTable from the rows it holds, but they
                         sum the table rather than belong to it. -->
                    <div id="dashboard-totals" class="totals-slot"></div>
                </div>

                <!-- Pagination -->
                <div v-if="pagination.totalPages.value > 1 || true" class="pagination">
                    <div class="pagination-left">
                        <span class="scan-count">
                            {{ $t('common.showing') }} {{ scans.length }} {{ $t('common.of') }} {{ totalScans }} {{ $t('common.scans') }}
                        </span>
                        <label class="items-per-page-label">{{ $t('common.itemsPerPage') }}</label>
                        <!-- Not a native select: the browser draws that list in
                             the system's own blue, and it was the one list on
                             the page the app could not dress. -->
                        <button
                            type="button"
                            class="items-per-page-select"
                            :class="{ open: perPageMenuOpen }"
                            @click.stop="togglePerPageMenu"
                        >
                            {{ perPageLabel(pagination.perPage.value) }}
                        </button>
                    </div>

                    <div class="pagination-center">
                        <button
                            :disabled="pagination.isFirstPage.value"
                            class="pagination-btn"
                            @click="goToPage(pagination.currentPage.value - 1)"
                        >
                            {{ $t('common.previous') }}
                        </button>

                        <div class="pagination-numbers">
                            <button
                                v-for="page in pagination.visiblePages.value"
                                :key="page"
                                :class="['pagination-number', { active: page === pagination.currentPage.value }]"
                                @click="goToPage(page)"
                            >
                                {{ page }}
                            </button>
                        </div>

                        <button
                            :disabled="pagination.isLastPage.value"
                            class="pagination-btn"
                            @click="goToPage(pagination.currentPage.value + 1)"
                        >
                            {{ $t('common.next') }}
                        </button>
                    </div>

                    <div class="pagination-right"></div>
                </div>
            </section>

            <!-- How many rows a page holds. In the body so the card it sits in
                 cannot clip it. -->
            <Teleport to="body">
                <div v-if="perPageMenuOpen" class="per-page-menu" :style="perPageMenuStyle">
                    <button
                        v-for="option in perPageOptions"
                        :key="option.value"
                        type="button"
                        class="per-page-menu-item"
                        :class="{ selected: option.value === pagination.perPage.value }"
                        @click="choosePerPage(option.value)"
                    >
                        {{ option.label }}
                    </button>
                </div>
            </Teleport>

            <!-- Export Modal -->
            <ExportModal
                v-if="showExportModal"
                :invoice-ids="exportIds"
                :company-name="selectedCompanyName"
                :company-id="selectedGlobalCompany"
                @close="showExportModal = false"
                @exported="handleExported"
                @updated="handleUpdated"
            />

            <!-- Bulk Delete Confirmation Modal -->
            <div v-if="showBulkDeleteModal" class="modal-overlay" @click="showBulkDeleteModal = false">
                <div class="modal-content" @click.stop>
                    <div class="modal-header">
                        <h3>{{ $t('invoices.bulkDeleteConfirm', { count: selection.selectedCount.value }, selection.selectedCount.value) }}</h3>
                        <button @click="showBulkDeleteModal = false" class="btn btn-icon">
                            <LineIcon name="x" :size="15" :stroke-width="1.8" />
                        </button>
                    </div>
                    <div class="modal-actions">
                        <button @click="executeBulkDelete" class="btn-confirm">{{ $t('common.yes', 'Yes') }}</button>
                        <button @click="showBulkDeleteModal = false" class="btn-cancel">{{ $t('common.no', 'No') }}</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import {ref, computed, onMounted, onUnmounted, watch} from 'vue';
import {isMobileDevice} from '@/utils/device';
import DateField from '@/components/DateField.vue';
import {useRouter, useRoute} from 'vue-router';
import {useI18n} from 'vue-i18n';
import {useScanStore} from '@/stores/scan';
import {useAuthStore} from '@/stores/auth';
import {useUiStore} from '@/stores/ui';
import {useCompanies} from '@/composables/useCompanyVendor';
import {usePagination} from '@/composables/usePagination';
import {validateCompanyOnInvoice} from '@/services/exportValidationService';
import {useFilters} from '@/composables/useFilters';
import {useSelection} from '@/composables/useSelection';
import {useConfirmDialog} from '@/composables/useConfirmDialog';
import Welcome from '@/pages/Welcome.vue';
import UploadQueue from '@/components/scanner/UploadQueue.vue';
import CompanySelector from '@/components/selectors/CompanySelector.vue';
import VendorSelector from '@/components/selectors/VendorSelector.vue';
import TypeSelector from '@/components/selectors/TypeSelector.vue';
import CustomSelect from '@/components/CustomSelect.vue';
import LineIcon from '@/components/icons/LineIcon.vue';
import ScansTable from '@/components/scanner/ScansTable.vue';
import ExportModal from '@/components/scanner/ExportModal.vue';
import api from '@/utils/api';

const router = useRouter();
const route = useRoute();
const {t} = useI18n();
const scanStore = useScanStore();
const authStore = useAuthStore();
const uiStore = useUiStore();
const {companies: companiesList, loadCompanies: loadCompaniesList} = useCompanies();
const {confirmBulkDelete, confirmDelete} = useConfirmDialog();

// Options for custom selects
const statusOptions = computed(() => [
    {value: '', label: t('invoices.allStatuses')},
    {value: 'completed', label: t('invoices.statusCompleted')},
    {value: 'processing', label: t('invoices.statusProcessing')},
    {value: 'failed', label: t('invoices.statusFailed')},
    {value: 'pending', label: t('invoices.statusPending')}
]);

const checkedOptions = computed(() => [
    {value: null, label: t('invoices.allInvoices')},
    {value: true, label: t('invoices.checkedOnly')},
    {value: false, label: t('invoices.uncheckedOnly')}
]);

const archivedOptions = computed(() => [
    {value: false, label: t('common.dontShow')},
    {value: true, label: t('common.show')}
]);

const paymentMethodOptions = computed(() => [
    {value: '', label: t('invoices.allPaymentMethods')},
    {value: 'Bank Transfer', label: t('paymentMethods.bank_transfer')},
    {value: 'Cash', label: t('paymentMethods.cash')},
    {value: 'Credit Card', label: t('paymentMethods.credit_card')},
    {value: 'Check', label: t('paymentMethods.check')},
    {value: 'Online Payment', label: t('paymentMethods.online_payment')},
    {value: 'Deferred Payment', label: t('paymentMethods.deferred_payment')},
    {value: 'Partial Payment', label: t('paymentMethods.partial_payment')},
    {value: 'Other', label: t('paymentMethods.other')}
]);

// Check if user is authenticated using auth store
const isAuthenticated = computed(() => authStore.isAuthenticated);

// Pagination
const pagination = usePagination({
    initialPage: parseInt(route.query.page) || 1,
    perPage: 999999 // Show all invoices by default
});

// Filters
const defaultFilters = {
    page: 1,
    status: '',
    sort: '',
    order: 'desc',
    search: '',
    company: '',
    companyId: null,
    vendorId: null,
    type: null,
    isChecked: null,
    dateFrom: '',
    dateTo: '',
    uploadDate: '',
    paymentMethod: '',
    showArchived: false  // Default: don't show archived invoices
};

const filterManager = useFilters({
    defaultFilters,
    onFilterChange: (newFilters, oldFilters) => {
        // Only reset page if filters other than 'page' changed
        const filtersChanged = Object.keys(newFilters).some(key => {
            if (key === 'page') return false; // Ignore page changes
            return newFilters[key] !== oldFilters?.[key];
        });

        if (filtersChanged) {
            pagination.resetPage();
        }
        loadScans();
    }
});

// Selection
const scans = computed(() => scanStore.scans);
const selection = useSelection({
    items: scans,
    idKey: 'id'
});

// Refs
const uploadQueueRef = ref(null);
const storedCompanyId = localStorage.getItem('selectedCompanyId');
const selectedGlobalCompany = ref(storedCompanyId ? parseInt(storedCompanyId) : null);
const tempCompanyId = ref(storedCompanyId ? parseInt(storedCompanyId) : null);
const topLevelCompanyId = ref(null); // Separate ref for top-level company filter
const loading = ref(true);
const showFilters = ref(false);
const showExportModal = ref(false);
const showBulkDeleteModal = ref(false);
const qrCodeDataUrl = ref(null);

/* Whether the upload form is open. UploadQueue owns the switch; the row it
   sits in is this page's, so the page follows it. */
const uploadFormVisible = ref(true);

/* Is there anything in the queue?
 *
 * The three cards stand to one height, which is right while they hold what
 * they normally hold. A queue of files is the one thing in the middle that
 * grows without limit, and the company card and the QR grew with it — two
 * mostly empty boxes as tall as a list of uploads. While the queue has
 * something in it the two stop stretching. */
const hasQueuedUploads = computed(() => (scanStore.uploadQueue?.length || 0) > 0);

/* Is there room for the QR code beside the form?
 *
 * Two different questions decide whether it is shown. isMobileDevice answers
 * "is this a phone", and on a phone the code is pointless — it is there to be
 * scanned BY one. This answers "is the window wide enough", because below the
 * breakpoint the three cards stack and the code would cost a screenful of
 * scrolling to say something a wide screen says for free.
 *
 * Kept reactive rather than read once: a window gets resized, and this has to
 * follow it. The code is only fetched when it is going to be shown.
 */
const qrViewport = window.matchMedia('(min-width: 769px)');
const qrHasRoom = ref(qrViewport.matches);
const followQrViewport = (event) => {
    qrHasRoom.value = event.matches;

    if (event.matches) {
        loadQrCode();
    }
};

const showQrCode = computed(() => uploadFormVisible.value && qrHasRoom.value && !isMobileDevice);
const companies = ref([]);
const queueItems = ref([]);
const selectedScans = ref([]);
const exportIds = ref([]);
const validationWarnings = ref([]);
// Persistent notice about the latest register import: which invoices are missing
// (holes in the consecutive № sequence) or a total that disagrees with "Сумарно".
const registerNotice = ref(null);
const rescanningMissing = ref(false);
const DISMISSED_REGISTER_KEY = 'dismissedRegisterNoticeId';
let searchTimeout = null;

// Computed
const totalScans = computed(() => scanStore.pagination?.total || 0);

// Totals of all displayed invoices (subtotal without VAT, VAT, grand total),
// grouped by currency — shown inline next to the scan count so the "Сумарно" is
// visible without scrolling to the bottom summary.
const scansTotalLabel = computed(() => {
    const totals = {};
    scans.value.forEach(s => {
        const c = s.currency || 'BGN';
        if (!totals[c]) totals[c] = { subtotal: 0, vat: 0, grand: 0 };
        totals[c].subtotal += parseFloat(s.subtotal) || 0;
        totals[c].vat += parseFloat(s.tax_amount) || 0;
        totals[c].grand += parseFloat(s.grand_total) || 0;
    });
    const fmt = (v) => v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return Object.entries(totals)
        .filter(([, t]) => t.grand > 0)
        .map(([c, t]) => `Без ДДС: ${fmt(t.subtotal)} · ДДС: ${fmt(t.vat)} · С ДДС: ${fmt(t.grand)} ${c}`)
        .join('  |  ');
});

const selectedCompanyName = computed(() => {
    if (!selectedGlobalCompany.value || !companiesList.value) return '';
    const company = companiesList.value.find(c => c.id === selectedGlobalCompany.value);
    return company ? company.name : '';
});

const selectedCompanyData = computed(() => {
    if (!selectedGlobalCompany.value || !companiesList.value) return null;
    return companiesList.value.find(c => c.id === selectedGlobalCompany.value) || null;
});

/**
 * The company's details, for the badge above the upload form.
 *
 * Only fields that are filled in: an empty one says nothing and a row of
 * blank labels would read as missing data rather than as unset.
 */
/* The company's record, in two groups.
 *
 * What it is registered as goes in a table of label and value: those are
 * references, read by looking one up, and they need their label to mean
 * anything.
 *
 * How to reach it is a list of plain lines. An address, a phone number, an
 * address with an @ in it and one starting with https:// each say what they
 * are — labelling them only repeated the obvious down the left of the card
 * and pushed the values themselves into a narrow column.
 */
const companyRegistry = computed(() => {
    const company = selectedCompanyData.value;
    if (!company) return [];

    return [
        ['companies.vatNumber', company.vat_number],
        ['companies.registrationNumber', company.registration_number],
        ['companies.taxOffice', company.tax_office],
    ]
        .filter(([, value]) => value)
        .map(([key, value]) => ({label: t(key), value}));
});

const companyContact = computed(() => {
    const company = selectedCompanyData.value;
    if (!company) return [];

    // Address, town and country are one place, not three fields.
    const place = [company.address, company.city, company.country].filter(Boolean).join(', ');

    return [place, company.phone, company.email, company.website].filter(Boolean);
});


// Sync pagination with total items from store
watch(totalScans, (newTotal) => {
    pagination.setTotalItems(newTotal);
});

// Sync currentPage from pagination to route
watch(() => pagination.currentPage.value, (newPage) => {
    if (newPage !== parseInt(route.query.page)) {
        filterManager.setFilter('page', newPage);
        filterManager.updateUrlParams();
    }
});

// Update select all state when scans change
watch(scans, () => {
    selection.updateSelectAllState();
    // Re-validate scans whenever they change
    validateLoadedScans();
}, {deep: true});

// Watch for changes in company validation settings
watch(companiesList, () => {
    // Re-validate when company data changes
    validateLoadedScans();
}, { deep: true });

// Watch for changes in user settings (per-user validation toggles)
watch(() => authStore.settings, () => {
    validateLoadedScans();
}, { deep: true });

// Watch for selected company changes
watch(selectedGlobalCompany, () => {
    // Re-validate when switching companies
    validateLoadedScans();
});

// Sync top-level company selector with filter's companyId
watch(() => filterManager.filters.value.companyId, (newCompanyId) => {
    topLevelCompanyId.value = newCompanyId;
});

// Clear selection when filters change
watch(() => [
    filterManager.filters.value.type,
    filterManager.filters.value.status,
    filterManager.filters.value.companyId,
    filterManager.filters.value.vendorId,
    filterManager.filters.value.isChecked,
    filterManager.filters.value.dateFrom,
    filterManager.filters.value.dateTo,
    filterManager.filters.value.uploadDate,
    filterManager.filters.value.paymentMethod,
    filterManager.filters.value.search
], () => {
    selection.clearSelection();
});

// Watch for company deletions - clear selection if selected company is deleted
watch(companiesList, (newCompanies) => {
    if (selectedGlobalCompany.value && newCompanies) {
        const companyExists = newCompanies.find(c => c.id === selectedGlobalCompany.value);
        if (!companyExists) {
            // Selected company was deleted, clear it
            selectedGlobalCompany.value = null;
            tempCompanyId.value = null;
            localStorage.removeItem('selectedCompanyId');
            filterManager.setFilter('companyId', null);
        }
    }
}, { deep: true });

// Initialize top-level company from filter
topLevelCompanyId.value = filterManager.filters.value.companyId;

// Counts the loads started here, so an overtaken one knows to keep quiet.
// The store guards its own data the same way; this guards the flag that
// decides whether the table or the spinner is on screen.
let scansLoad = 0;

const loadScans = async () => {
    // Don't load scans if no company is selected
    if (!selectedGlobalCompany.value) {
        loading.value = false;
        return;
    }

    const token = ++scansLoad;
    loading.value = true;
    try {
        const params = {
            ...pagination.getPaginationParams(),
            ...filterManager.getApiParams(),
            company_id: selectedGlobalCompany.value // Enforce company filter
        };

        // Remove show_archived from params as it's not used by backend
        delete params.show_archived;

        console.log('Loading scans with params:', params);

        // Exclude archived invoices from dashboard by default
        // Only include them if the user explicitly chooses to show them
        if (!filterManager.filters.value.showArchived) {
            params.exclude_status = 'archived';
        }

        await scanStore.fetchScans(params);

        // An overtaken load must not validate either: the rows it would read
        // belong to the request that overtook it.
        if (token !== scansLoad) {
            return;
        }

        // Validate loaded scans against company
        validateLoadedScans();
    } catch (error) {
        console.error('Failed to load scans:', error);
    } finally {
        if (token === scansLoad) {
            loading.value = false;
        }
    }
};

const validateLoadedScans = () => {
    // Clear previous warnings
    validationWarnings.value = [];
    
    console.log('🚀 validateLoadedScans called');
    console.log('🚀 selectedGlobalCompany:', selectedGlobalCompany.value);
    console.log('🚀 companiesList:', companiesList.value);
    
    if (!selectedGlobalCompany.value) {
        console.log('❌ No company selected, skipping validation');
        return;
    }
    
    // Find the selected company
    const company = companiesList.value.find(c => c.id === selectedGlobalCompany.value);
    
    console.log('🚀 Found company:', company);
    console.log('🚀 Validation enabled?', authStore.validateCompanyOnScan);
    
    if (!company || !authStore.validateCompanyOnScan) {
        console.log('⏭️ Validation skipped:', { 
            hasCompany: !!company, 
            companyName: company?.name,
            validationEnabled: authStore.validateCompanyOnScan 
        });
        return;
    }
    
    console.log('✅ Starting validation for company:', company.name);
    console.log('✅ Company data:', { 
        name: company.name, 
        vat_number: company.vat_number,
        validate_company_on_scan: authStore.validateCompanyOnScan 
    });
    
    // Check all loaded scans
    const allScans = scanStore.scans || [];

    allScans.forEach((scan) => {
        // Check if invoice data is in scan.result or directly on scan object
        const invoiceData = scan.result || scan;

        if (invoiceData && (invoiceData.buyer_name || invoiceData.seller_name)) {
            const validation = validateCompanyOnInvoice({ result: invoiceData }, company, { enabled: true });

            if (!validation.isValid) {
                const invoiceNum = validation.invoiceNumber || scan.invoice_number || 'Unknown';

                // Add to warnings array
                validationWarnings.value.push({
                    id: scan.id,
                    invoiceNumber: invoiceNum
                });
            }
        }
    });

    if (validationWarnings.value.length > 0) {
        console.warn(`⚠️ Found ${validationWarnings.value.length} invoices without company "${company.name}"`);
        console.warn('⚠️ These invoices will show in the warning banner');
    } else {
        console.log('✅ All invoices contain company "${company.name}"');
    }
}

const handleCompanySelection = (company) => {
    if (company && company.id) {
        selectedGlobalCompany.value = company.id;
        // Set the company filter permanently
        filterManager.setFilter('companyId', company.id);
        // Load scans for this company
        loadScans();
        // Store in localStorage for persistence
        localStorage.setItem('selectedCompanyId', company.id.toString());
    }
};

const handleUploadQueueCompanyChange = (companyId) => {
    if (companyId) {
        selectedGlobalCompany.value = companyId;
        tempCompanyId.value = companyId;
        // Set the company filter
        filterManager.setFilter('companyId', companyId);
        // Load scans for this company
        loadScans();
        // Store in localStorage for persistence
        localStorage.setItem('selectedCompanyId', companyId.toString());
    } else {
        // If company is cleared, switch back to selection screen
        switchCompany();
    }
};

const switchCompany = () => {
    router.push('/companies');
};

const refreshScans = async () => {
    // Add a small delay to ensure backend has processed the scan
    await new Promise(resolve => setTimeout(resolve, 500));
    await loadScans();
};

const applyFilters = () => {
    filterManager.applyFilters();
};

const clearFilters = () => {
    filterManager.clearFilters();
};

const handleSearchInput = () => {
    if (searchTimeout) clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
        applyFilters();
    }, 500);
};

const handleCompanyChange = () => {
    filterManager.setFilter('vendorId', null);
    applyFilters();
};

const handleTopLevelCompanyChange = (company) => {
    const companyId = company?.id || null;
    filterManager.setFilter('companyId', companyId);
    filterManager.setFilter('vendorId', null); // Reset vendor when company changes
    applyFilters();
};

const handleSortChange = ({ sortBy, sortOrder }) => {
    filterManager.setFilter('sort', sortBy);
    filterManager.setFilter('order', sortOrder);
    applyFilters();
};

const goToPage = (page) => {
    if (pagination.goToPage(page)) {
        loadScans();
        window.scrollTo({top: 0, behavior: 'smooth'});
    }
};

const handlePerPageChange = () => {
    pagination.setPerPage(pagination.perPage.value);
    loadScans();
};

/* How many rows a page holds, picked from a list the app draws itself.
 *
 * Same shape as the income/expense picker in the table: one menu, placed in
 * page coordinates under the button that opened it, closed by a click
 * elsewhere, by Escape, or by any scroll — it cannot follow what moves.
 */
const PER_PAGE_STEPS = [12, 300, 500, 1000, 999999];

const perPageMenuOpen = ref(false);
const perPageMenuStyle = ref({});

const perPageLabel = (value) => (value === 999999 ? t('common.all') : String(value));

const perPageOptions = computed(() => {
    const steps = PER_PAGE_STEPS.includes(pagination.perPage.value)
        ? PER_PAGE_STEPS
        : [pagination.perPage.value, ...PER_PAGE_STEPS];

    return steps.map((value) => ({ value, label: perPageLabel(value) }));
});

const closePerPageMenu = () => {
    perPageMenuOpen.value = false;
};

const togglePerPageMenu = (event) => {
    if (perPageMenuOpen.value) {
        closePerPageMenu();
        return;
    }

    const box = event.currentTarget.getBoundingClientRect();
    perPageMenuStyle.value = {
        top: `${box.bottom + 4}px`,
        left: `${box.left}px`,
        minWidth: `${Math.max(box.width, 86)}px`,
    };
    perPageMenuOpen.value = true;
};

const choosePerPage = (value) => {
    closePerPageMenu();
    if (value === pagination.perPage.value) return;

    pagination.perPage.value = value;
    handlePerPageChange();
};

const handlePerPageOutside = (event) => {
    if (!perPageMenuOpen.value) return;
    if (event.target.closest('.per-page-menu') || event.target.closest('.items-per-page-select')) return;
    closePerPageMenu();
};

const handlePerPageKey = (event) => {
    if (event.key === 'Escape') closePerPageMenu();
};

// After a register import, always show ALL invoices (these imports create many rows).
const handleRegisterCompleted = async () => {
    pagination.setPerPage(999999);
    await refreshScans();
    // A fresh import should always surface its notice, even if an earlier register
    // was dismissed.
    localStorage.removeItem(DISMISSED_REGISTER_KEY);
    await fetchRegisterIssues();
};

// Load the latest register import's unresolved issues (missing invoices / total
// mismatch) so the notice persists across reloads, not just as a one-off alert.
const fetchRegisterIssues = async () => {
    try {
        const { data } = await api.get('/api/invoices/register-issues');
        const n = data?.notice;
        if (!n) {
            registerNotice.value = null;
            return;
        }
        if (localStorage.getItem(DISMISSED_REGISTER_KEY) === String(n.register_id)) {
            registerNotice.value = null;
            return;
        }
        registerNotice.value = n;
    } catch (e) {
        // Non-critical — leave any existing notice untouched.
    }
};

const dismissRegisterNotice = () => {
    if (registerNotice.value?.register_id) {
        localStorage.setItem(DISMISSED_REGISTER_KEY, String(registerNotice.value.register_id));
    }
    registerNotice.value = null;
};

// Re-scan ONLY the pages holding the still-missing invoices and add them, without
// re-processing the whole register.
const rescanMissingInvoices = async () => {
    const id = registerNotice.value?.register_id;
    if (!id || rescanningMissing.value) return;

    rescanningMissing.value = true;
    try {
        const { data } = await api.post(`/api/invoices/${id}/rescan-missing`);
        await refreshScans();

        if ((data?.missing_count ?? 0) > 0) {
            // Still some missing → refresh the notice with the new (shorter) list.
            registerNotice.value = {
                ...registerNotice.value,
                missing_numbers: data.missing_numbers || [],
                missing_count: data.missing_count,
                child_count: data.child_count ?? registerNotice.value.child_count,
            };
            alert(`Досканирани ${data.recovered} фактури. Все още липсват ${data.missing_count}:\n${(data.missing_numbers || []).join(', ')}`);
        } else {
            registerNotice.value = null;
            alert(data?.recovered > 0
                ? `Готово: досканирани ${data.recovered} фактури. Вече няма липсващи.`
                : 'Няма липсващи фактури.');
        }
    } catch (e) {
        alert(e?.response?.data?.message || 'Досканирането се провали. Опитайте пак.');
    } finally {
        rescanningMissing.value = false;
    }
};

const toggleSelect = (scanId) => {
    selection.toggleSelect(scanId);
};

const toggleSelectAll = () => {
    selection.toggleSelectAll();
};

const exportScans = (format) => {
    if (selection.selectedCount.value === 0) {
        alert('Please select at least one scan to export');
        return;
    }
    const selectedSet = new Set(selection.selectedItems.value);
    exportIds.value = scans.value.map(s => s.id).filter(id => selectedSet.has(id));
    showExportModal.value = true;
};

const exportAll = async (format) => {
    // For TXT format, show modal to select NAP type
    if (format === 'txt') {
        // Show modal with TXT options
        selectedScans.value = scans.value.map(scan => scan.id);
        showExportModal.value = true;
        return;
    }

    // For other formats, export directly
    const formatMap = {
        'original': null,
        'pdf': 'pdf',
        'xlsx': 'html'
    };

    const apiFormat = formatMap[format];

    if (format === 'original') {
        alert('Downloading original files for all scans...');
        // TODO: Implement original files bulk download
        return;
    }

    if (!apiFormat) {
        console.error('Unknown format:', format);
        return;
    }

    const invoiceIds = scans.value.map(scan => scan.id);

    if (invoiceIds.length === 0) {
        alert('No scans to export');
        return;
    }

    try {
        const response = await api.post('/api/scans/export/bulk', {
            invoice_ids: invoiceIds,
            format: apiFormat
        }, {
            responseType: 'blob'
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;

        const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
        const filename = `invoices_${timestamp}.zip`;

        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Export failed:', error);
        alert('Failed to export. Please try again.');
    }
};

const downloadAllPdf = async () => {
    const invoiceIds = scans.value.map(scan => scan.id);
    if (invoiceIds.length === 0) {
        alert('No scans to export');
        return;
    }

    try {
        const response = await api.post('/api/scans/export/bulk', {
            invoice_ids: invoiceIds,
            format: 'pdf'
        }, {
            responseType: 'blob'
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
        link.setAttribute('download', `invoices_${timestamp}.zip`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Export failed:', error);
        alert('Failed to export PDF. Please try again.');
    }
};

const downloadAllNapPurchases = async () => {
    const invoiceIds = scans.value.map(scan => scan.id);
    if (invoiceIds.length === 0) {
        alert('No scans to export');
        return;
    }

    try {
        const response = await api.post('/api/scans/export/bulk', {
            invoice_ids: invoiceIds,
            format: 'txt_pokupki'
        }, {
            responseType: 'blob'
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
        link.setAttribute('download', `POKUPKI_${timestamp}.txt`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Export failed:', error);
        alert('Failed to export NAP Purchases. Please try again.');
    }
};

const downloadAllNapSales = async () => {
    const invoiceIds = scans.value.map(scan => scan.id);
    if (invoiceIds.length === 0) {
        alert('No scans to export');
        return;
    }

    try {
        const response = await api.post('/api/scans/export/bulk', {
            invoice_ids: invoiceIds,
            format: 'txt_prodagbi'
        }, {
            responseType: 'blob'
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
        link.setAttribute('download', `PRODAGBI_${timestamp}.txt`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Export failed:', error);
        alert('Failed to export NAP Sales. Please try again.');
    }
};

const downloadAllHtml = async () => {
    const invoiceIds = scans.value.map(scan => scan.id);
    if (invoiceIds.length === 0) {
        alert('No scans to export');
        return;
    }

    try {
        const response = await api.post('/api/scans/export/bulk', {
            invoice_ids: invoiceIds,
            format: 'html'
        }, {
            responseType: 'blob'
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
        link.setAttribute('download', `invoices_${timestamp}.zip`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Export failed:', error);
        alert('Failed to export HTML. Please try again.');
    }
};

const downloadAllOriginal = () => {
    alert('Bulk download of original files is not yet implemented. Please select individual scans and use the export modal.');
};

const openExportAllModal = () => {
    exportIds.value = scans.value.map(scan => scan.id);
    showExportModal.value = true;
};

const handleExportScan = (scanIds) => {
    // Set export IDs without modifying selection state
    if (Array.isArray(scanIds)) {
        exportIds.value = scanIds;
    } else {
        exportIds.value = [scanIds];
    }
    showExportModal.value = true;
};

const handleDeleteScan = async (scanId) => {
    console.log('Delete clicked for scan:', scanId);

    try {
        const result = await scanStore.deleteScan(scanId);
        if (result.success === false) {
            throw new Error(result.error || 'Failed to delete');
        }
        await loadScans();
        // Refresh subscription to update usage counter
        console.log('Refreshing subscription after deletion...');
        await authStore.fetchUser();
        console.log('Subscription refreshed. New count:', authStore.subscription?.scans_used);
    } catch (error) {
        console.error('Failed to delete scan:', error);
        alert('Failed to delete scan. Please try again.');
    }
};

const handleTypeChange = async ({scanId, type}) => {
    try {
        await api.put(`/api/invoices/${scanId}`, {type});
        // Reload scans to reflect the change
        await loadScans();
    } catch (error) {
        console.error('Failed to update invoice type:', error);
        alert('Failed to update invoice type. Please try again.');
    }
};

const deleteSelected = async () => {
    if (selection.selectedCount.value === 0) {
        return;
    }

    showBulkDeleteModal.value = true;
};

const executeBulkDelete = async () => {
    showBulkDeleteModal.value = false;

    try {
        // Delete in bulk (one request per chunk) instead of one request per scan.
        const ids = [...selection.selectedItems.value];
        const chunkSize = 300;
        for (let i = 0; i < ids.length; i += chunkSize) {
            const chunk = ids.slice(i, i + chunkSize);
            await api.post('/api/invoices/bulk-delete', { invoice_ids: chunk });
        }

        // Clear selection and reload
        selection.clearSelection();
        await loadScans();
    } catch (error) {
        console.error('Failed to delete scans:', error);
        alert('Failed to delete some scans. Please try again.');
    }
};

const archiveSelected = async () => {
    if (selection.selectedCount.value === 0) {
        return;
    }

    if (!confirm(`${t('common.archiveConfirm')} ${selection.selectedCount.value} ${t('common.invoices')}?`)) {
        return;
    }

    try {
        // Archive each selected scan
        for (const scanId of selection.selectedItems.value) {
            await api.post(`/api/invoices/${scanId}/archive`);
        }

        // Clear selection and reload
        selection.clearSelection();
        await loadScans();
    } catch (error) {
        console.error('Failed to archive scans:', error);
        alert('Failed to archive some scans. Please try again.');
    }
};

const updateQueueItems = (items) => {
    queueItems.value = items;
};

const cancelUpload = (itemId) => {
    // The upload queue component will handle the actual cancellation
    queueItems.value = queueItems.value.filter(item => item.id !== itemId);
};

const handleExported = () => {
    // Don't clear selection after export
    showExportModal.value = false;
};

const handleUpdated = async () => {
    // Refresh scans list after changes in ExportModal
    await loadScans();
};

const loadQrCode = async () => {
    // Nothing shows it on a phone or in a narrow window, and fetching it
    // spends a login token that expires in five minutes unused.
    if (isMobileDevice || !qrHasRoom.value) {
        return;
    }

    if (!qrCodeDataUrl.value) {
        try {
            const response = await api.get('/api/invoice/qr/show', {
                responseType: 'blob'
            });
            qrCodeDataUrl.value = URL.createObjectURL(response.data);
        } catch (error) {
            console.error('Failed to load QR code:', error);
        }
    }
};

onMounted(async () => {
    // Fetch user data first if not already loaded
    if (!authStore.user) {
        await authStore.fetchUser();
    }

    await loadCompaniesList();
    loadQrCode();

    // Surface any unresolved register issues (missing invoices / total mismatch).
    // Must run before the company branches below, which can return early.
    fetchRegisterIssues();

    // Check for company ID in URL query parameter first
    const urlCompanyId = route.query.companyId ? parseInt(route.query.companyId) : null;

    if (urlCompanyId) {
        // Verify company exists
        const companyExists = companiesList.value.some(c => c.id === urlCompanyId);
        if (companyExists) {
            selectedGlobalCompany.value = urlCompanyId;
            tempCompanyId.value = urlCompanyId;
            localStorage.setItem('selectedCompanyId', urlCompanyId.toString());
            filterManager.setFilter('companyId', urlCompanyId);
            await loadScans();
            return;
        }
    }

    // Check if stored company ID is still valid
    if (selectedGlobalCompany.value && companiesList.value) {
        const companyExists = companiesList.value.find(c => c.id === selectedGlobalCompany.value);
        if (companyExists) {
            filterManager.setFilter('companyId', selectedGlobalCompany.value);
            await loadScans();
        } else {
            // Stored company no longer exists, clear it
            selectedGlobalCompany.value = null;
            tempCompanyId.value = null;
            localStorage.removeItem('selectedCompanyId');
        }
    }
});

// Watch for URL query parameter changes to switch company
watch(() => route.query.companyId, async (newCompanyId) => {
    if (newCompanyId) {
        const companyId = parseInt(newCompanyId);
        const companyExists = companiesList.value.some(c => c.id === companyId);
        if (companyExists && companyId !== selectedGlobalCompany.value) {
            selectedGlobalCompany.value = companyId;
            tempCompanyId.value = companyId;
            localStorage.setItem('selectedCompanyId', companyId.toString());
            filterManager.setFilter('companyId', companyId);
            // Clear current selections
            selection.clearSelection();
            await loadScans();
        }
    }
});

// Keyboard shortcuts for bulk delete modal
const handleBulkDeleteKeydown = (event) => {
    if (showBulkDeleteModal.value) {
        if (event.key === 'Escape') {
            showBulkDeleteModal.value = false;
        } else if (event.key === ' ' || event.key === 'Spacebar') {
            event.preventDefault();
            executeBulkDelete();
        }
    }
};

onMounted(() => {
    qrViewport.addEventListener('change', followQrViewport);
    window.addEventListener('keydown', handleBulkDeleteKeydown);
    document.addEventListener('pointerdown', handlePerPageOutside, true);
    document.addEventListener('keydown', handlePerPageKey);
    window.addEventListener('scroll', closePerPageMenu, true);
    window.addEventListener('resize', closePerPageMenu);
});

onUnmounted(() => {
    qrViewport.removeEventListener('change', followQrViewport);
    window.removeEventListener('keydown', handleBulkDeleteKeydown);
    document.removeEventListener('pointerdown', handlePerPageOutside, true);
    document.removeEventListener('keydown', handlePerPageKey);
    window.removeEventListener('scroll', closePerPageMenu, true);
    window.removeEventListener('resize', closePerPageMenu);
});

watch(() => showBulkDeleteModal.value, (newVal) => {
    if (!newVal) {
        // Cleanup if needed
    }
});
</script>

<style scoped>
.home-page {
    min-height: 100vh;
    background: var(--bg-gray);
    padding: 24px 0;
}

.page-container {
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 24px;
}

/* Company Selection Screen (STATE A) */
/* Taken out of the page's container so the photograph reaches every edge.
   No z-index: that leaves it above the page's own content but still under the
   navigation, which is sticky at 100 and whose blur lets the image through. */
.company-selection-screen {
    position: fixed;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    background-image: url('/img/blog-image.jpg');
    background-size: cover;
    background-position: center;
    overflow: auto;
}

/* A wash over the photograph. Without it the card's white and the page's own
   greys sit on whatever happens to be behind them, and the greeting stops
   being readable the moment the image changes. */
.company-selection-screen::before {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(248, 250, 252, 0.82);
    backdrop-filter: blur(2px);
}

/* The card is above the wash, not under it. */
.company-selection-screen > * {
    position: relative;
    z-index: 1;
}

.selection-card {
    background: white;
    border-radius: 20px;
    padding: 32px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
    text-align: center;
    /* It holds a logo, one line of greeting and one field — 600px was a
       width inherited from a card with far more in it. */
    max-width: 460px;
    width: 100%;
}

/* Height only, so the square mark keeps its proportions. Centred by its own
   margins rather than by the card's text-align, which a block-level image
   would not follow. */
.selection-logo {
    display: block;
    height: 56px;
    width: auto;
    margin: 0 auto;
}

.selection-icon {
    margin-bottom: 20px;
}

.selection-title {
    display: flex;
    flex-direction: column;
    gap: 2px;
    font-size: 24px;
    font-weight: 700;
    text-wrap: pretty;
    word-break: break-all;
    color: var(--text-primary);
    margin: 0 0 24px 0;
}

.selection-brand {
    font-size: 24px;
    font-weight: 700;
}

/* Lighter and smaller than the name above it: the greeting is the second
   thing read, not the first. */
.selection-greeting {
    font-size: 16px;
    font-weight: 500;
    color: var(--text-secondary);
}

.selection-selector {
    margin-bottom: 24px;
}

.selection-hint {
    font-size: 14px;
    color: var(--text-secondary);
    margin: 0;
}

.link-button {
    background: none;
    border: none;
    color: var(--primary);
    font-weight: 600;
    cursor: pointer;
    text-decoration: underline;
    padding: 0;
    font-size: 14px;
}

.link-button:hover {
    color: #0284c7;
}

/* Company card (STATE B)
 *
 * It was a blue band across the page, then a badge parked loose at the top
 * left — which, once the upload form moved to the left edge, landed on top of
 * it. It is a card now, the same make as the one holding the QR code, and it
 * holds its column in the row instead of floating over one.
 *
 * Everything in it is centred on the card's middle: the name, the record
 * under it and the swap button below that.
 */
.company-header-bar {
    flex-shrink: 0;
    width: 260px;
    background: white;
    /* The same corner as every other card on the page: the panels under it,
       the table and the totals are all 12px. This row was 16 and read as
       belonging to a different set. */
    border-radius: 12px;
    padding: 16px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    text-align: center;
}

.company-info {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
}

.company-name-badge {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    /* Full width, or the block shrinks to its longest line and the two
       columns inside it stop meeting the card's own edges. */
    width: 100%;
}

/* The record as a table of pairs: the label against the card's left edge,
 * the value against its right. Two straight edges instead of fifteen lines
 * each centred on its own width. The name and the button stay centred — they
 * speak for the whole card, not for one field. */
.company-details {
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: 100%;
    text-align: left;
}

.company-detail {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 10px;
    font-size: 11px;
    line-height: 15px;
}

.company-detail-label {
    flex-shrink: 0;
}

/* The contact lines, under a hairline that separates them from the registry
   above without adding a second heading to a card this small. */
.company-contact {
    display: flex;
    flex-direction: column;
    gap: 2px;
    width: 100%;
    padding-top: 8px;
    border-top: 1px solid var(--gray-lighter, #f1f5f9);
    text-align: left;
}

.company-contact-line {
    font-size: 11px;
    line-height: 15px;
    color: var(--text-secondary);
    overflow-wrap: anywhere;
}

.company-detail-label {
    color: var(--text-light);
    text-transform: uppercase;
    letter-spacing: 0.3px;
    font-weight: 600;
}

.company-detail-value {
    color: var(--text-secondary);
    min-width: 0;
    text-align: right;
    overflow-wrap: anywhere;
}

.company-name-badge svg {
    flex-shrink: 0;
}

.company-name {
    font-size: 15px;
    font-weight: 700;
    color: var(--primary);
}

.btn-switch-company {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 6px 12px;
    background: transparent;
    border: 1px solid var(--border);
    border-radius: 8px;
    color: var(--primary);
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
}

.btn-switch-company:hover {
    background: var(--primary-light, #e0f2fe);
    border-color: var(--primary);
}

.btn-switch-company svg {
    flex-shrink: 0;
    display: block;
    vertical-align: middle;
}

/* Upload Section */
.upload-section {
    margin-bottom: 32px;
}

/* The drop zone and the QR code, side by side.
 *
 * The form takes what is left; the QR keeps a column of its own, since a
 * code has to stay big enough for a phone to read it. Narrow screens put
 * them back one above the other, where the row is dropped. */
.upload-row {
    display: flex;
    align-items: stretch;
    gap: 16px;
}

/* One height for all three.
 *
 * Left to themselves the three columns each stood as tall as their own
 * contents — 243, 246 and 252px — which reads as three boxes that nearly
 * line up, and nearly is worse than not at all. They stretch to the tallest
 * instead, and the drop zone in the middle takes the slack so its white box
 * reaches the bottom with the cards either side.
 */
.upload-row__form {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
}

.upload-row__form :deep(.upload-queue) {
    flex: 1;
    display: flex;
    flex-direction: column;
}

.upload-row__form :deep(.upload-zone-slot) {
    flex: 1;
    display: flex;
}

.upload-row__form :deep(.upload-zone) {
    flex: 1;
}

/* …until the queue fills. Then the two outer cards take their own height and
   sit at the top of the row, and only the middle column grows. */
.upload-row--queued {
    align-items: flex-start;
}

/* With a height to fill, the card's own parts take their places: the name
   and the record at the top, the swap button at the foot. */
.upload-row:not(.upload-row--collapsed) .company-info {
    height: 100%;
    justify-content: space-between;
}

/* The code and its line sit in the middle of whatever height the row has. */
.upload-row:not(.upload-row--collapsed) .qr-code-section {
    justify-content: center;
}

/* Hidden form: the row comes down to one line.
 *
 * Hiding the form empties the whole row, so the only thing left to say is
 * which company the page is showing — that has to stay on screen, or the
 * table below it belongs to nobody. The card drops its record and its QR
 * neighbour and keeps the name, with the button to bring the form back at
 * the other end of the same line.
 */
.upload-row--collapsed {
    align-items: center;
}

/* Both parts of the collapsed row stand 40px tall — the bar was 54 and the
   button 34, which beside each other read as two unrelated things. */
.upload-row--collapsed .company-header-bar {
    flex: 1;
    width: auto;
    min-width: 0;
    height: 40px;
    padding: 0 14px;
    display: flex;
    align-items: center;
    text-align: left;
}

.upload-row--collapsed .company-info {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
    min-width: 0;
}

/* The name and the registry run along the line rather than stacking: there
   is a card's worth of room here and only one line to fill, so the facts
   that were worth a card when it was open are worth stating here too. */
.upload-row--collapsed .company-name-badge {
    width: auto;
    flex: 1;
    min-width: 0;
    flex-direction: row;
    align-items: baseline;
    gap: 16px;
    overflow: hidden;
}

.upload-row--collapsed .company-details {
    display: flex;
    flex-direction: row;
    width: auto;
    gap: 16px;
    text-align: left;
    white-space: nowrap;
    overflow: hidden;
}

.upload-row--collapsed .company-detail {
    gap: 5px;
}

/* The contact block stays out: an address and a web address are long, and
   cut in half by an ellipsis they say less than nothing. */
.upload-row--collapsed .company-contact {
    display: none;
}

.upload-row--collapsed .upload-row__form {
    flex: 0 0 auto;
}

/* The button carries 20px under it for the form that used to follow it;
   with nothing following, that is what pushed it off the bar's line. */
.upload-row--collapsed .upload-row__form :deep(.btn-toggle-form) {
    height: 40px;
    margin-bottom: 0;
}

/* QR Code Section */
.qr-code-section {
    flex-shrink: 0;
    width: 260px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    background: white;
    border-radius: 12px;
    padding: 16px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    text-align: center;
}

/* Smaller, but not at the code's expense.
 *
 * The squares are what a camera reads, and they are coarser now that the
 * code is drawn at the standard error correction rather than the highest —
 * 43 across instead of 61. That buys the size: at 100px the symbol gives
 * 2.3px per square, where the old 116px one gave 1.9.
 *
 * The white around it is the quiet zone a scanner needs to find the symbol,
 * so the padding stays wide even though the card shrank.
 */
.qr-code-card {
    flex-shrink: 0;
    width: 120px;
    height: 120px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: white;
    border: 1px solid var(--border);
    /* A corner inside a corner is drawn tighter than the one around it, or
       the two look like they are fighting. */
    border-radius: 8px;
    padding: 9px;
}

.qr-code-image {
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.qr-code-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    color: var(--text-secondary);
}

.qr-code-placeholder p {
    font-size: 13px;
    margin: 0;
}

.spinner {
    width: 32px;
    height: 32px;
    border: 3px solid var(--border);
    border-top-color: var(--primary);
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.qr-info {
    flex: 1;
}

.qr-title {
    font-size: 15px;
    font-weight: 700;
    color: var(--text-primary);
    margin: 0 0 4px 0;
}

.qr-description {
    font-size: 13px;
    color: var(--text-secondary);
    margin: 0;
    line-height: 1.5;
}

/* Scans Section */
.scans-section {
    margin-top: 32px;
    margin-bottom: 48px;
}

/* No longer a card itself — just the stack that spaces the three apart. */
.table-container {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 32px;
}

/* The cards' edge, dark enough to be seen where it matters.
 *
 * It was #e2e8f0, and the table's own header band is #f8fafc — the same
 * colour as the page behind the card. So along the top that line separated
 * two identical greys, and around the corner, where a 1px line is drawn at
 * half strength across the curve, it disappeared altogether: the card read
 * as having no rounded corners at all.
 *
 * #dde3ea is what the rows inside already divide themselves with, so the
 * card is now outlined in the same weight as its own contents.
 */
.panel {
    background: var(--white);
    border: 1px solid #dde3ea;
    border-radius: 12px;
    overflow: hidden;
}

/* The card with the pickers in it must not clip.
 *
 * overflow: hidden is there to keep the rounded corners honest around the
 * table's rows. On this card it also cut off the open company and document
 * lists at the card's own bottom edge — which looked like the list opening
 * behind the warning below it, when in fact it was being trimmed to the box.
 *
 * Nothing inside this one has a background of its own to spill past the
 * corners, so it can be left open.
 */
.panel--controls {
    overflow: visible;
}

/* Collapse to nothing when empty, so the gap around them does not double. */
.alerts-slot:empty,
.totals-slot:empty {
    display: none;
}

/* The banners carry their own margin inside the old single card; between
   cards the stack's gap does that job. */
.alerts-slot > * {
    margin-bottom: 0 !important;
}

.alerts-slot > * + * {
    margin-top: 8px;
}

/* Table Header */
.table-header {
    padding: 20px 24px;
    border-bottom: 2px solid var(--border);
    background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
}

.header-actions {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
}

.btn-manual-entry,
.btn-companies,
.btn-filter {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px 20px;
    background: white;
    border: none;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary);
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    height: 45px;
}

.btn-manual-entry {
    background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
    color: white;
    border-color: #8b5cf6;
    box-shadow: 0 4px 12px rgba(139, 92, 246, 0.25);
}

.btn-manual-entry:hover {
    background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(139, 92, 246, 0.35);
}

.btn-filter {
    background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
    color: white;
    border: none;
    box-shadow: 0 4px 12px rgba(14, 165, 233, 0.25);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

.btn-filter svg {
    display: block;
    flex-shrink: 0;
}

.btn-filter-text {
    font-size: 14px;
    font-weight: 600;
    color: white;
}

.btn-filter.active {
    background: linear-gradient(135deg, #0284c7 0%, #0369a1 100%);
    box-shadow: 0 4px 12px rgba(14, 165, 233, 0.35);
}

.btn-companies:hover {
    border-color: var(--primary);
    color: var(--primary);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(14, 165, 233, 0.15);
}

.btn-filter:hover {
    background: linear-gradient(135deg, #0284c7 0%, #0369a1 100%);
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(14, 165, 233, 0.35);
}

/* Filters Row */
.filters-row {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 18px;
    padding: 24px;
    border-bottom: 2px solid var(--border);
    background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
}

.filter-item {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.filter-label {
    font-size: 11px;
    font-weight: 700;
    color: #4b5563;
    text-transform: uppercase;
    letter-spacing: 0.8px;
}

/* Hide the standalone "Filter:" label if it exists */
.filters-row > .filter-label {
    display: none;
}

.filter-select,
.filter-input {
    padding: 10px 14px;
    border: 1px solid var(--border);
    border-radius: 8px;
    font-size: 14px;
    color: var(--text-primary);
    background: white;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-height: 42px;
}

.filter-select.active-filter,
.filter-input.active-filter {
    border: 2px solid #0ea5e9;
    background: rgba(14, 165, 233, 0.05);
}

/* The date fields are the same box as every other filter.
 *
 * Only the calendar they open is ours; the field itself has no business
 * looking different from the pickers it stands in a row with — it was a
 * narrow 108px button while they filled their column.
 */
.filter-date {
    display: block;
    width: 100%;
}

.filter-date :deep(.date-field-input) {
    width: 100%;
    border: 1px solid var(--border);
    border-radius: 8px;
    color: var(--text-primary);
    background-color: white;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.filter-date :deep(.date-field-input):hover {
    border-color: var(--gray-light);
}

.filter-date :deep(.date-field-input).open {
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
}

/* A date that is set marks its field, the way the other filters do. */
.filter-date.active-filter :deep(.date-field-input) {
    border: 2px solid #0ea5e9;
    background-color: rgba(14, 165, 233, 0.05);
}

.filter-clear-container {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
}

.btn-clear-filters {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    padding: 10px 18px;
    border: 1px solid var(--border);
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    color: var(--text-secondary);
    background: white;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;
    min-height: 42px;
}

.btn-clear-filters:hover {
    border-color: #ef4444;
    color: white;
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.btn-clear-filters svg {
    flex-shrink: 0;
    font-size: 16px;
}

.filter-select:hover,
.filter-input:hover {
    border-color: var(--gray-light);
}

.filter-select:focus,
.filter-input:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
}

.filter-select option {
    background: white;
    color: var(--text-primary);
}

.filter-select option:checked,
.filter-select option:hover,
.filter-select option:focus {
    background: var(--primary-light) !important;
    color: var(--text-primary) !important;
}

/* Actions Row */
/* No background of its own — this is what was squaring off the card's
 * corners.
 *
 * The row carried a white-to-grey fade. A background is painted as a
 * rectangle with square corners, and this card does not clip its children
 * (it cannot: the pickers in it drop lists that have to leave the box), so
 * that rectangle covered the rounded corners the card draws underneath it.
 *
 * The fade ran from #ffffff to #f8fafc across one row — barely visible, and
 * it cost the corners. The card's own white does the same job. */
.actions-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    gap: 16px;
}

.sort-group {
    display: flex;
    align-items: center;
    gap: 14px;
}

/* stretch, not flex-start: flex-start sizes each child to its own
   content, so the company field grew and shrank with the name in it — shifting
   the type picker beside it and leaving a gap under the label when the name
   was short. Pinned widths hold the row still whatever is selected. */
.company-selector-group,
.type-selector-group {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
}

/* Wide enough for a full company name, which the type selector never needs. */
.company-selector-group {
    flex: 0 0 240px;
    min-width: 240px;
}

.type-selector-group {
    flex: 0 0 190px;
    min-width: 190px;
}

.filter-export-group {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-left: auto;
}

.actions-row .btn-filter {
    margin-left: 0;
}

.sort-label {
    font-size: 11px;
    font-weight: 700;
    color: #4b5563;
    text-transform: uppercase;
    letter-spacing: 0.8px;
}

.sort-select {
    padding: 11px 16px;
    border: 2px solid var(--border);
    border-radius: 10px;
    font-size: 14px;
    font-weight: 500;
    color: var(--text-primary);
    background: white;
    cursor: pointer;
    min-width: 180px;
    transition: all 0.2s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.sort-select:hover {
    border-color: #0ea5e9;
    box-shadow: 0 2px 8px rgba(14, 165, 233, 0.15);
}

.sort-select:focus {
    outline: none;
    border-color: #0ea5e9;
    box-shadow: 0 0 0 4px rgba(14, 165, 233, 0.15);
}

.export-actions {
    display: flex;
    gap: 12px;
}

.btn-export-single {
    display: flex;
    align-items: center;
    gap: 9px;
    padding: 12px 24px;
    border: none;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 700;
    color: white;
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
    height: 45px;
}

.btn-export-single:hover {
    background: linear-gradient(135deg, #059669 0%, #047857 100%);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4);
}

.btn-export {
    padding: 10px 20px;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    color: white;
    cursor: pointer;
    transition: all 0.2s ease;
}

.btn-original {
    background: #0ea5e9;
}

.btn-original:hover {
    background: #0284c7;
}

.btn-pdf {
    background: #ef4444;
}

.btn-pdf:hover {
    background: #dc2626;
}

.btn-txt {
    background: #64748b;
}

.btn-txt:hover {
    background: #475569;
}

.btn-nap {
    background: #8b5cf6;
}

.btn-nap:hover {
    background: #7c3aed;
}

.btn-nap-alt {
    background: #ec4899;
}

.btn-nap-alt:hover {
    background: #db2777;
}

.btn-html {
    background: #f59e0b;
}

.btn-html:hover {
    background: #d97706;
}

.btn-xlsx {
    background: #10b981;
}

.btn-xlsx:hover {
    background: #059669;
}

/* Select Row */
/* White, like the rows under it. Three tinted bands stacked — this, the
   column headers and the date strip — made the top of the table look like
   three separate tables; only the headers keep a fill now. */
.select-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 20px;
    border-bottom: 1px solid var(--border);
    background: var(--white);
    min-height: 0;
    position: relative; /* anchor the centred scan-count/totals */
}

.select-all-section {
    display: flex;
    align-items: center;
    gap: 8px;
}

.select-all-section input[type="checkbox"] {
    width: 16px;
    height: 16px;
    cursor: pointer;
}

.select-all-section label {
    font-size: 13px;
    color: var(--text-secondary);
    cursor: pointer;
}

.bulk-actions {
    display: flex;
    align-items: center;
    gap: 12px;
}

.selected-count {
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary);
    padding: 8px 12px;
    background: var(--white);
    border-radius: 6px;
}

.btn-bulk {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 18px;
    border: none !important;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
}

.btn-bulk-export {
    background: #10b981 !important;
    background-color: #10b981 !important;
    color: white !important;
    border: none !important;
    outline: none !important;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
}

.btn-bulk-export:hover {
    background: #059669 !important;
    background-color: #059669 !important;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15) !important;
}

.btn-bulk-archive {
    background: #f59e0b !important;
    background-color: #f59e0b !important;
    color: white !important;
    border: none !important;
    outline: none !important;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
}

.btn-bulk-archive:hover {
    background: #d97706 !important;
    background-color: #d97706 !important;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15) !important;
}

.btn-bulk-delete {
    background: #ef4444 !important;
    background-color: #ef4444 !important;
    color: white !important;
    border: none !important;
    outline: none !important;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
}

.btn-bulk-delete:hover {
    background: #dc2626 !important;
    background-color: #dc2626 !important;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15) !important;
}

.scan-count {
    font-size: 12px;
    color: var(--text-light);
}

.scan-totals-center {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    white-space: nowrap;
    text-align: center;
    font-size: 12px;
    color: var(--text-light);
}

/* Pagination */
.pagination {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    margin-top: 32px;
    padding-top: 32px;
    border-top: 1px solid var(--border);
}

.pagination-left {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
}

.pagination-center {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 12px;
    flex: 1;
}

.pagination-right {
    flex: 1;
}

.items-per-page-label {
    font-size: 14px;
    color: var(--text-secondary);
    font-weight: 500;
}

.items-per-page-select {
    padding: 8px 12px;
    background: white;
    border: 1px solid var(--border);
    border-radius: 8px;
    font-family: inherit;
    font-size: 14px;
    color: var(--text-primary);
    cursor: pointer;
    transition: all 0.2s ease;
    /* Room for the chevron the stylesheet draws on the right. */
    padding-right: 26px;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 12 12' fill='none' stroke='%23374151' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m2.5 4.5 3.5 3.5 3.5-3.5'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 9px center;
}

.items-per-page-select:hover {
    border-color: var(--primary);
}

.items-per-page-select:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 1px;
}

.items-per-page-select.open {
    border-color: var(--primary);
    color: var(--primary);
    /* Turned over and in the same blue as the text, while the list is down. */
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 12 12' fill='none' stroke='%232b82f0' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m2.5 7.5 3.5-3.5 3.5 3.5'/%3E%3C/svg%3E");
}

/* The list, dressed like the pickers at the top of the page rather than by
   the operating system. */
.per-page-menu {
    position: fixed;
    z-index: 1000;
    background: var(--white, #fff);
    border: 1px solid var(--border, #e2e8f0);
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    padding: 0;
}

.per-page-menu-item {
    display: block;
    width: 100%;
    padding: 7px 14px;
    border: none;
    background: transparent;
    font-family: inherit;
    font-size: 12px;
    font-weight: 500;
    color: var(--text-primary);
    text-align: left;
    white-space: nowrap;
    cursor: pointer;
    transition: background 0.15s ease;
}

.per-page-menu-item + .per-page-menu-item {
    border-top: 1px solid var(--gray-lighter, #f1f5f9);
}

.per-page-menu-item:hover {
    background: var(--primary-light, #e0f2fe);
}

.per-page-menu-item.selected {
    background: var(--primary-light, #e0f2fe);
    font-weight: 600;
}

.pagination-btn {
    padding: 10px 20px;
    background: white;
    border: 1px solid var(--border);
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    color: var(--text-primary);
    cursor: pointer;
    transition: all 0.2s ease;
}

.pagination-btn:hover:not(:disabled) {
    border-color: var(--primary);
    color: var(--primary);
}

.pagination-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.pagination-numbers {
    display: flex;
    gap: 6px;
}

.pagination-number {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: white;
    border: 1px solid var(--border);
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    color: var(--text-primary);
    cursor: pointer;
    transition: all 0.2s ease;
}

.pagination-number:hover {
    border-color: var(--primary);
    color: var(--primary);
}

.pagination-number.active {
    background: var(--primary);
    border-color: var(--primary);
    color: white;
}

@media (max-width: 768px) {
    .scans-section {
        padding: 12px;
    }

    .upload-row {
        flex-direction: column;
    }

    /* Stacked, the two cards are no longer columns of a row — they are the
       page. A 260px card under a full-width one reads as something that did
       not finish loading. */
    .company-header-bar {
        width: 100%;
    }

    /* Collapsed, the bar keeps the name and nothing else.
     *
     * On a wide screen it also carries the VAT number, the registration
     * number and the tax office, which fit beside the name with room to
     * spare. At 390px they do not: the line overflowed and pushed the name
     * itself out of sight, leaving a bar that said "ДДС НОМЕР …" and no
     * company at all — which is the one thing it exists to say. */
    .upload-row--collapsed .company-details {
        display: none;
    }

    /* Stacked, both parts take the width. The collapsed bar is told to grow
       along the row's main axis, which on a phone runs downwards — so it grew
       taller and stayed as narrow as its text, floating in the middle of the
       screen with the button under it. */
    .upload-row--collapsed .company-header-bar {
        width: 100%;
        height: auto;
        min-height: 40px;
    }

    .upload-row--collapsed .upload-row__form {
        width: 100%;
    }

    /* The component's own box has to take the width too, or the button
       inside it is 100% of something narrower than the card above it. */
    .upload-row--collapsed .upload-row__form :deep(.upload-queue) {
        width: 100%;
        align-self: stretch;
    }

    .upload-row--collapsed .upload-row__form :deep(.btn-toggle-form) {
        width: 100%;
    }

    .qr-code-section {
        width: 100%;
        padding: 16px;
    }

    .qr-code-card {
        width: 160px;
        height: 160px;
    }

    .qr-info {
        text-align: center;
    }

    .filters-section {
        flex-direction: column;
    }

    .filters-grid {
        grid-template-columns: 1fr;
    }

    .bulk-actions-bar {
        flex-direction: column;
        gap: 12px;
    }

    .table-header {
        padding: 12px;
    }

    .header-actions {
        width: 100%;
        flex-wrap: wrap;
    }

    .btn-manual-entry,
    .btn-companies,
    .btn-filter {
        flex: 1;
        min-width: calc(50% - 6px);
        padding: 10px 12px;
        font-size: 13px;
        gap: 6px;
        height: auto;
    }

    .filters-row {
        grid-template-columns: 1fr;
        padding: 16px 12px;
        gap: 12px;
    }

    .filters-row .filter-item {
        width: 100%;
    }

    .actions-row {
        flex-direction: column;
        align-items: stretch;
        padding: 12px;
        gap: 12px;
    }

    .company-selector-group,
    .type-selector-group {
        flex: 1 1 auto;
        min-width: 0;
    }

    .filter-export-group {
        display: flex;
        gap: 12px;
        width: 100%;
    }

    .filter-export-group .btn-filter,
    .filter-export-group .export-actions {
        flex: 1 1 50%;
        min-width: 0;
        max-width: 50%;
    }

    .filter-export-group .btn-filter {
        margin-left: 0;
    }

    .filter-export-group .btn-export-single {
        width: 100%;
    }

    .sort-group,
    .type-selector-group {
        flex-direction: column;
        align-items: stretch;
        gap: 8px;
    }

    .sort-label {
        font-size: 10px;
    }

    .sort-select {
        width: 100%;
        min-width: auto;
        padding: 10px 12px;
        font-size: 13px;
    }

    .export-actions {
        width: 100%;
    }

    .btn-export-single {
        width: 100%;
        justify-content: center;
        padding: 10px 16px;
        font-size: 13px;
        height: auto;
    }

    .select-row {
        flex-direction: column;
        align-items: stretch;
        padding: 12px;
        gap: 12px;
    }

    .select-all-section {
        justify-content: center;
    }

    .select-all-section label {
        font-size: 12px;
    }

    .bulk-actions {
        flex-direction: column;
        width: 100%;
        gap: 8px;
    }

    .selected-count {
        text-align: center;
    }

    .btn-bulk {
        width: 100%;
        justify-content: center;
        font-size: 13px;
        padding: 8px 14px;
    }

    .scan-count {
        text-align: center;
        font-size: 12px;
    }

    .scan-totals-center {
        position: static;
        transform: none;
        white-space: normal;
        text-align: center;
        font-size: 12px;
    }

    .bulk-actions-left,
    .bulk-actions-right {
        width: 100%;
        justify-content: center;
    }

    .pagination {
        flex-direction: column;
        gap: 16px;
    }

    .pagination-left,
    .pagination-center {
        width: 100%;
        justify-content: center;
    }

    .pagination-numbers {
        gap: 4px;
    }

    .pagination-number {
        width: 36px;
        height: 36px;
        font-size: 12px;
    }

    .pagination-btn {
        padding: 8px 12px;
        font-size: 12px;
    }

    .items-per-page-label {
        font-size: 12px;
    }

    .items-per-page-select {
        padding: 6px 10px;
        font-size: 12px;
    }
}

/* Bulk Delete Modal */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

.modal-content {
    background: white;
    border-radius: 12px;
    padding: 24px;
    max-width: 400px;
    width: 90%;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
    position: relative;
}

.modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
    padding-bottom: 16px;
    border-bottom: 1px solid #e2e8f0;
    position: relative;
}

.modal-header h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #1e293b;
    padding-right: 40px;
}

.modal-header .btn {
    position: absolute;
    top: 0;
    right: 0;
    flex-shrink: 0;
}

.modal-actions {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
}

.btn-confirm,
.btn-cancel {
    padding: 10px 20px;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
}

.btn-confirm {
    background: #10b981;
    color: white;
}

.btn-confirm:hover {
    background: #059669;
}

.btn-cancel {
    background: #f1f5f9;
    color: #4b5563;
}

.btn-cancel:hover {
    background: #e2e8f0;
}

/* Persistent register-missing notice */
.register-notice {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    margin: 0 0 1rem;
    padding: 0.9rem 1rem;
    background: #fef3c7;
    border: 1px solid #fcd34d;
    border-radius: 10px;
    color: #78350f;
}

.register-notice__icon {
    font-size: 1.25rem;
    line-height: 1.4;
}

.register-notice__body {
    flex: 1 1 auto;
    min-width: 0;
}

.register-notice__title {
    font-weight: 700;
    margin-bottom: 0.25rem;
}

.register-notice__nums {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 0.85rem;
    word-break: break-word;
    background: rgba(255, 255, 255, 0.6);
    border-radius: 6px;
    padding: 0.35rem 0.5rem;
    margin-bottom: 0.4rem;
}

.register-notice__hint {
    font-size: 0.82rem;
    opacity: 0.85;
}

.register-notice__actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex: 0 0 auto;
    align-self: center; /* vertically centre the button within the tall banner */
}

.register-notice__btn {
    background: #d97706;
    color: #fff;
    border: none;
    border-radius: 8px;
    padding: 0.5rem 0.85rem;
    font-weight: 600;
    font-size: 0.85rem;
    cursor: pointer;
    white-space: nowrap;
}

.register-notice__btn:hover:not(:disabled) {
    background: #b45309;
}

.register-notice__btn:disabled {
    opacity: 0.65;
    cursor: default;
}

.register-notice__dismiss {
    background: transparent;
    border: none;
    color: #78350f;
    font-size: 1.4rem;
    line-height: 1;
    cursor: pointer;
    padding: 0 0.25rem;
}

/* ── The dashboard's dress ─────────────────────────────────────────────
 *
 * One card, one 12px radius, hairline edges, and controls at the table's
 * scale: 13px labels in 32px boxes, one filled button and the rest quiet.
 * A dialogue is opened from the table and should read as the same product.
 *
 * Last in the file on purpose — these do not delete what the modal set for
 * itself above, they settle the frame, the heading and the buttons. */
.modal-content,
.modal-container,
.modal-content-large {
    border: 1px solid #dde3ea;
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
}

.modal-header {
    padding: 12px 14px;
    border-bottom: 1px solid #dde3ea;
    background: #fff;
}

.modal-header h2,
.modal-header h3,
.modal-header h4,
.modal-title {
    margin: 0;
    font-size: 14px;
    font-weight: 700;
    color: #1e293b;
}

.modal-subtitle {
    margin: 1px 0 0;
    font-size: 11px;
    font-weight: 400;
    color: #4b5563;
}

.modal-body {
    padding: 12px 14px;
}

.modal-footer {
    padding: 10px 14px;
    gap: 8px;
    background: #fff;
    border-top: 1px solid #dde3ea;
}

/* The close: a mark on its own, with the lift the rest of the app has. */
.modal-header .btn-icon,
.btn-close,
.modal-close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    min-width: 26px;
    padding: 0;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 8px;
    color: #4b5563;
    box-shadow: none;
    transition: all 0.15s ease;
}

.modal-header .btn-icon:hover,
.btn-close:hover,
.modal-close:hover {
    background: transparent;
    color: #0697e4;
    transform: translateY(-2px);
}

/* The buttons: the same box everywhere, filled only where something is
   being committed. */
.btn-primary,
.btn-secondary,
.btn-cancel,
.btn-cancel-edit,
.btn-save,
.btn-save-edit,
.btn-confirm,
.btn-danger,
.btn-success,
.btn-add {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    height: 32px;
    padding: 0 14px;
    border-radius: 8px;
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    line-height: 1;
    box-shadow: none;
    transition: all 0.15s ease;
}

.btn-secondary,
.btn-cancel,
.btn-cancel-edit {
    background: #fff;
    border: 1px solid #dde3ea;
    color: #1e293b;
}

.btn-secondary:hover:not(:disabled),
.btn-cancel:hover:not(:disabled),
.btn-cancel-edit:hover:not(:disabled) {
    background: #fff;
    border-color: #0697e4;
    color: #0697e4;
    box-shadow: none;
    transform: none;
}

.btn-primary,
.btn-confirm,
.btn-save,
.btn-save-edit,
.btn-add {
    background: #0697e4;
    border: 1px solid #0697e4;
    color: #fff;
}

.btn-primary:hover:not(:disabled),
.btn-confirm:hover:not(:disabled),
.btn-save:hover:not(:disabled),
.btn-save-edit:hover:not(:disabled),
.btn-add:hover:not(:disabled) {
    background: #0b84c7;
    border-color: #0b84c7;
    box-shadow: none;
    transform: none;
}

.btn-success {
    background: #10b981;
    border: 1px solid #10b981;
    color: #fff;
}

.btn-success:hover:not(:disabled) {
    background: #0ea371;
    border-color: #0ea371;
    box-shadow: none;
    transform: none;
}

.btn-danger {
    background: #e04545;
    border: 1px solid #e04545;
    color: #fff;
}

.btn-danger:hover:not(:disabled) {
    background: #c93a3a;
    border-color: #c93a3a;
    box-shadow: none;
    transform: none;
}

/* The row of answers under a question — "delete these seven?" — is a footer
   like any other: a hairline above it and the two buttons at its end. */
.modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 10px 14px;
    border-top: 1px solid #dde3ea;
    background: #fff;
}

/* A question and its two answers: one line between them, not two, and the
   one that deletes carries the bin's red rather than the app's blue. */
.modal-overlay .modal-content .modal-header {
    border-bottom: none;
    padding-bottom: 4px;
}

.modal-actions .btn-confirm {
    background: #e04545;
    border-color: #e04545;
}

.modal-actions .btn-confirm:hover:not(:disabled) {
    background: #c93a3a;
    border-color: #c93a3a;
}

/* The corners.
 *
 * The card is rounded but does not clip, so the header's own white — and the
 * footer's — was painted square and stood a pixel or two outside the curve.
 * The bands carry the same radius on the side they sit on. Clipping the card
 * instead would cut off anything a dialogue drops below its edge. */
.modal-header {
    border-radius: 12px 12px 0 0;
}

.modal-footer,
.modal-actions {
    border-radius: 0 0 12px 12px;
}

/* ── One hover for a mark on its own ───────────────────────────────────
 *
 * Wherever a button is just an icon — a row's edit or delete, a field's
 * helper, a dialogue's close — hovering turns the mark its own colour and
 * lifts it two pixels. Nothing is drawn behind it. Written last, and the
 * danger variant written with both of its classes, because the rules being
 * answered are themselves .btn-icon.btn-icon-danger. */
.btn-icon:hover:not(:disabled) {
  background: transparent;
  border-color: transparent;
  color: var(--primary, #0697e4);
  box-shadow: none;
  transform: translateY(-2px);
}

.btn-icon.btn-icon-danger:hover,
.btn-icon.btn-icon-danger:hover:not(:disabled) {
  background: transparent;
  border-color: transparent;
  color: #e04545;
  box-shadow: none;
  transform: translateY(-2px);
}

.btn-icon svg {
  transition: transform 0.15s ease;
}
</style>
